import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./style.css";
import Container from "containers/anx1/SharedModule/DropdownTreeContainer";

// Import from our common actions
import {
  getEntityList,
  getGstinByEntity,
  setSelectedEntity,
  setSelectedGstin,
  setSelectedReturnPeriod,
} from "./common/actions";
import {
  makeSelectEntityList,
  makeSelectProfileData,
  makeSelectSelectedEntity,
  makeSelectSelectedGstin,
  makeSelectSelectedReturnPeriod,
  makeSelectLoaderStatus,
} from "./common/selectors";

const Filter = ({ activeTab = "1", singleGstinSelection = false }) => {
  // Redux state - using our common reducer
  const dispatch = useDispatch();
  const entityList = useSelector(makeSelectEntityList());
  const profileData = useSelector(makeSelectProfileData());
  const selectedEntity = useSelector(makeSelectSelectedEntity());
  const selectedGstinList = useSelector(makeSelectSelectedGstin());
  const selectedReturnPeriod = useSelector(makeSelectSelectedReturnPeriod());
  const loading = useSelector(makeSelectLoaderStatus());

  // Load entity list on component mount
  useEffect(() => {
    dispatch(getEntityList());
  }, [dispatch]);

  // Event handlers
  const handleEntity = async (currentNode, selectedNode) => {
    // Enhanced clear detection for react-dropdown-tree-select
    const isClearing = (
      !selectedNode || 
      selectedNode.length === 0 || 
      (Array.isArray(selectedNode) && selectedNode.every(node => !node || !node.value || node.value === '')) ||
      (currentNode && currentNode._clearTriggered) ||
      (currentNode === null || currentNode === undefined)
    );
    
    if (isClearing) {
      dispatch(setSelectedEntity(''));
      // Clear GSTIN based on selection type
      dispatch(setSelectedGstin(singleGstinSelection ? '' : []));
      return;
    }
    
    const entityId = selectedNode[0]?.value || '';
    dispatch(setSelectedEntity(entityId));
    
    if (entityId) {
      // Clear previous GSTIN selection when changing entity
      dispatch(setSelectedGstin(singleGstinSelection ? '' : []));
      
      // Fetch GSTIN list for selected entity
      try {
        await dispatch(getGstinByEntity(entityId));
      } catch (error) {
        console.error('Error fetching GSTIN list:', error);
      }
    }
  };

  const handleGstin = (currentNode, selectedNodes) => {
    // Handle clearing GSTIN selection
    const isClearing = (
      !selectedNodes || 
      selectedNodes.length === 0 || 
      (Array.isArray(selectedNodes) && selectedNodes.every(node => !node || !node.value || node.value === '')) ||
      (currentNode && currentNode._clearTriggered) ||
      (currentNode === null || currentNode === undefined)
    );
    
    if (isClearing) {
      dispatch(setSelectedGstin(singleGstinSelection ? '' : []));
      return;
    }

    if (singleGstinSelection) {
      // Single GSTIN selection (similar to GSTR6)
      const gstin = selectedNodes.length > 0 ? (selectedNodes[0].gstin || selectedNodes[0].GSTIN || selectedNodes[0].value) : "";
      dispatch(setSelectedGstin(gstin));
    } else {
      // Multiple GSTIN selection (existing behavior)
      let gstinArray = [];
      
      if (selectedNodes[0]?.label === "All GSTIN") {
        // Select all GSTIN
        gstinArray = profileData.map((item) => item.gstin || item.GSTIN);
      } else {
        // Select specific GSTIN
        gstinArray = selectedNodes.map((item) => item.gstin || item.GSTIN || item.value);
      }
      
      dispatch(setSelectedGstin(gstinArray));
    }
  };

  const handleFYChange = (currentNode, selectedNode) => {
    // Handle clearing FY selection
    if (!selectedNode || selectedNode.length === 0) {
      dispatch(setSelectedReturnPeriod(''));
      return;
    }
    
    const fyValue = selectedNode[0]?.value || '';
    dispatch(setSelectedReturnPeriod(fyValue));
  };

  // Generate Financial Year options
  const generateFYOptions = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-based, so April = 3
    
    // Determine current FY - Indian FY starts from April
    let currentFY;
    if (currentMonth >= 3) { // April (3) to March next year
      currentFY = currentYear;
    } else { // January to March - previous FY
      currentFY = currentYear - 1;
    }
    
    const fyOptions = [];
    // Generate FY from 2019-20 to current FY
    for (let year = 2019; year <= currentFY; year++) {
      const fyLabel = `${year}-${year+ 1}`;
      fyOptions.push({
        label: fyLabel,
        value: fyLabel,
        checked: selectedReturnPeriod === fyLabel
      });
    }
    
    return fyOptions.reverse(); // Show latest FY first
  };

  // Get entity list with selection state
  const getEntityMasterList = () => {
    if (!entityList || entityList.length === 0) {
      return [];
    }
    
    // Use checked property correctly for react-dropdown-tree-select
    const result = entityList.map((item) => ({
      ...item,
      label: item.label,
      value: item.value,
      checked: selectedEntity ? item.value === selectedEntity : false,
      disabled: false // Explicitly set disabled to false to prevent component disabling
    }));
    
    return result;
  };

  // Get profile data formatted for dropdown
  const getProfileData = () => {
    if (!profileData || profileData.length === 0) {
      return [];
    }
    let modifiedProfile = profileData.filter(v => v.profileType === "GST");


    if (singleGstinSelection) {
      // Single selection format (similar to GSTR6)
      return modifiedProfile.map((v) => ({ 
        ...v, 
        label: v.gstin || v.GSTIN, 
        value: v.gstin || v.GSTIN,
        gstin: v.gstin || v.GSTIN,
        checked: (selectedGstinList === (v.gstin || v.GSTIN)) && (selectedGstinList !== "")
      }));
    } else {
      // Multiple selection format (existing behavior)
      const newProfile = modifiedProfile.map((v) => ({ 
        ...v, 
        label: v.gstin || v.GSTIN, 
        value: v.gstin || v.GSTIN,
        checked: Array.isArray(selectedGstinList) && selectedGstinList.includes(v.gstin || v.GSTIN)
      }));

      return [
        {
          label: "All GSTIN",
          value: "All GSTIN",
          checked: Array.isArray(selectedGstinList) && selectedGstinList.length === newProfile.length && newProfile.length > 0,
          tagClassName: "special",
          children: newProfile,
          expanded: true, // ✅ Keep the GSTIN list expanded/open
        },
      ];
    }
  };

  return (
    <div style={{ position: "relative", zIndex: 100 }}>
      <form>
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between",
          gap: "16px",
          // alignItems: "end",
          position: "relative",
          zIndex: 100
        }}>
          {/* Entity Dropdown */}
          <div className="dropdown-container-gstr1" style={{ 
            width: "32%", 
            position: "relative", 
            zIndex: 103 
          }}>
            <label
              style={{
                fontFamily: "GT-Walsheim-medium",
                fontSize: "12px",
                color: "#333",
                display: "block",
                marginBottom: "4px",
                fontWeight: "500"
              }}
            >
              Select Entity *
            </label>
            <Container
              data={getEntityMasterList()}
              mode="simpleSelect"
              onChange={handleEntity}
              isDisabled={false}
              showDropDown={true}
              clearSearchOnClose={false}
              keepTreeOnSearch={false}
              showPartiallySelected={false}
              enableClear={true}
              keepChildrenOnSearch={false}
              inlineSearchInput={true}
              style={{ position: "relative", zIndex: 103 }}
            />
          </div>

          {/* GSTIN Dropdown */}
          <div className="dropdown-container-gstr1" style={{ 
            width: "32%", 
            position: "relative", 
            zIndex: 102 
          }}>
            <label
              style={{
                fontFamily: "GT-Walsheim-medium",
                fontSize: "12px",
                color: "#333",
                display: "block",
                marginBottom: "4px",
                fontWeight: "500"
              }}
            >
              Select GSTIN *
            </label>
            <Container
              data={getProfileData()}
              mode={singleGstinSelection ? "simpleSelect" : "multiSelect"}
              onChange={handleGstin}
              isDisabled={!selectedEntity || loading || profileData.length === 0}
              showDropDown={true}
              clearSearchOnClose={false}
              keepTreeOnSearch={true}
              showPartiallySelected={!singleGstinSelection}
              enableClear={true}
              keepChildrenOnSearch={true}
              inlineSearchInput={true}
              keepOpenOnSelect={!singleGstinSelection}
              expandOnSearch={true}
              collapseOnSelect={singleGstinSelection}
              style={{ position: "relative", zIndex: 102 }}
            />
          </div>

          {/* Financial Year Dropdown */}
          <div
            style={{ 
            //   paddingTop: "0.63em", 
              width: "32%",
              position: "relative", 
              zIndex: 101 
            }}
            className="dropdown-container-gstr1"
          >
            <label
              style={{
                fontFamily: "GT-Walsheim-medium",
                fontSize: "12px",
                color: "#333",
                display: "block",
                marginBottom: "4px",
                fontWeight: "500"
              }}
            >
              Select Financial Year *
            </label>
            {/* <InputLabel
              id="demo-simple-select-autowidth-label"
              className="text_label"
              style={{
                fontFamily: "GT-Walsheim-medium",
                fontSize: "12px",
                color: "#333",
                top: "10px",
                fontWeight: "500",
                // marginBottom: "4px"
              }}
            >
              Select Financial Year *
            </InputLabel> */}
            
            <Container
              data={generateFYOptions()}
              mode="simpleSelect"
              onChange={handleFYChange}
              isDisabled={false}
              showDropDown={true}
              clearSearchOnClose={false}
              keepTreeOnSearch={false}
              showPartiallySelected={false}
              enableClear={true}
              keepChildrenOnSearch={false}
              inlineSearchInput={true}
              style={{ position: "relative", zIndex: 101 }}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default Filter;